import { useState, useEffect } from 'react'
import moment from 'moment'
import { request } from '@/lib/request'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
  BarChart3,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'

interface Task {
  name: string
  plat: string
  status: string
  window_status?: string
  window_start_time?: string
  scheduled_start_time?: string
  cooldown_until?: string
  last_check_time?: string
  last_schedule_attempt?: string
  redistributed_at?: string
  new_window_target_start?: string
  interrupted?: boolean
  balance?: number
  [key: string]: any
}

interface GanttItem {
  taskName: string
  platform: string
  windowStatus: string
  runningStatus: string
  displayType: string
  startTime: string
  endTime: string
  fullStartTime: moment.Moment
  fullEndTime: moment.Moment
  startOffsetPercent: number
  durationPercent: number
  windowColor: string
  runningColor: string
  isConflict: boolean
  interrupted?: boolean
  balance?: number
  task: Task
}

export default function TaskManage() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [timeWindowCenter, setTimeWindowCenter] = useState(moment())
  const [timeWindowSize, setTimeWindowSize] = useState(24) // 小时
  const [autoFollowNow, setAutoFollowNow] = useState(true)
  const [showDensityHeatmap, setShowDensityHeatmap] = useState(false)

  // 获取任务数据
  const fetchTasks = async () => {
    try {
      const resp = await request('/system/api/query/task', {
        page_size: 1000,
        page_num: 1,
        query_filter: {},
      })
      if (resp && resp.code === 0 && resp.data) {
        setTasks(resp.data.list || [])
      }
    } catch (error) {
      console.error('Failed to load tasks:', error)
    }
  }

  useEffect(() => {
    fetchTasks()
    const interval = setInterval(() => {
      if (autoFollowNow) {
        setTimeWindowCenter(moment())
      }
      fetchTasks()
    }, 30000) // 每30秒刷新
    return () => clearInterval(interval)
  }, [autoFollowNow])

  // 计算时间范围
  const getDateRange = () => {
    const center = autoFollowNow ? moment() : moment(timeWindowCenter)
    const halfWindow = timeWindowSize / 2
    return {
      start: center.clone().subtract(halfWindow, 'hours'),
      end: center.clone().add(halfWindow, 'hours'),
      center,
      totalHours: timeWindowSize,
    }
  }

  // 生成 Gantt 数据
  const generateGanttData = (): GanttItem[] => {
    if (!tasks.length) return []

    const { start: rangeStart, end: rangeEnd } = getDateRange()
    const ganttData: GanttItem[] = []

    tasks.forEach((task) => {
      let windowStart: moment.Moment, windowEnd: moment.Moment
      let displayType = 'window'
      let windowStatus = task.window_status || 'unknown'

      // 情况1: 有活动窗口时间
      if (task.window_start_time) {
        windowStart = moment(task.window_start_time)
        windowEnd = moment(task.window_start_time).add(8, 'hours')
        displayType = 'window'
      }
      // 情况2: 有预定启动时间但没有窗口时间
      else if (task.scheduled_start_time) {
        windowStart = moment(task.scheduled_start_time)
        windowEnd = moment(task.scheduled_start_time).add(8, 'hours')
        displayType = 'scheduled'
        windowStatus = 'scheduled'
      }
      // 情况3: 有冷却时间的任务
      else if (task.cooldown_until) {
        const cooldownEnd = moment(task.cooldown_until)
        const now = moment()

        if (cooldownEnd.isBefore(now)) {
          windowStart = now
          windowEnd = now.clone().add(2, 'hours')
          displayType = 'waiting'
          windowStatus = 'waiting'
        } else if (cooldownEnd.isBetween(rangeStart, rangeEnd)) {
          windowStart = cooldownEnd
          windowEnd = cooldownEnd.clone().add(1, 'hour')
          displayType = 'cooldown'
          windowStatus = 'cooldown'
        } else {
          return // 冷却时间不在范围内，跳过
        }
      }
      // 情况4: 有最后检查时间的停止任务
      else if (task.last_check_time || task.last_schedule_attempt) {
        const lastTime = moment(task.last_check_time || task.last_schedule_attempt)
        if (lastTime.isBetween(rangeStart, rangeEnd)) {
          windowStart = lastTime
          windowEnd = lastTime.clone().add(2, 'hours')
          displayType = 'waiting'
          windowStatus = 'waiting'
        } else {
          return
        }
      } else {
        return
      }

      // 检查窗口是否与显示范围有交集
      if (windowEnd.isBefore(rangeStart) || windowStart.isAfter(rangeEnd)) {
        return
      }

      // 计算实际显示的开始和结束时间
      const displayStart = windowStart.isBefore(rangeStart)
        ? rangeStart.clone()
        : windowStart.clone()
      const displayEnd = windowEnd.isAfter(rangeEnd) ? rangeEnd.clone() : windowEnd.clone()

      // 计算位置百分比
      const totalMinutes = rangeEnd.diff(rangeStart, 'minutes')
      const startOffset = displayStart.diff(rangeStart, 'minutes')
      const duration = displayEnd.diff(displayStart, 'minutes')

      const statusColor: Record<string, string> = {
        available: '#52c41a',
        active: '#1890ff',
        expired: '#d9d9d9',
        scheduled: '#faad14',
        cooldown: '#f0f0f0',
        waiting: '#ff85c0',
        unknown: '#bfbfbf',
      }

      ganttData.push({
        taskName: task.name,
        platform: task.plat,
        windowStatus: windowStatus,
        runningStatus: task.status,
        displayType: displayType,
        startTime: displayStart.format('MM-DD HH:mm'),
        endTime: displayEnd.format('HH:mm'),
        fullStartTime: displayStart,
        fullEndTime: displayEnd,
        startOffsetPercent: (startOffset / totalMinutes) * 100,
        durationPercent: (duration / totalMinutes) * 100,
        windowColor: statusColor[windowStatus] || '#d9d9d9',
        runningColor: task.status === 'runing' ? '#52c41a' : '#ff4d4f',
        isConflict: Boolean(
          windowStatus === 'active' && (task.redistributed_at || task.new_window_target_start)
        ),
        interrupted: task.interrupted,
        balance: task.balance,
        task: task,
      })
    })

    return ganttData.sort((a, b) => a.fullStartTime.valueOf() - b.fullStartTime.valueOf())
  }

  // 时间轴操作
  const moveTimeWindow = (direction: number) => {
    const moveAmount = timeWindowSize * 0.25
    setTimeWindowCenter(timeWindowCenter.clone().add(direction * moveAmount, 'hours'))
    setAutoFollowNow(false)
  }

  const jumpToNow = () => {
    setTimeWindowCenter(moment())
    setAutoFollowNow(true)
  }

  const zoomTimeWindow = (factor: number) => {
    const newSize = Math.max(1, Math.min(168, timeWindowSize * factor))
    setTimeWindowSize(newSize)
  }

  // 生成时间标签
  const generateTimeLabels = () => {
    const { start: rangeStart, totalHours } = getDateRange()
    const labels = []
    const labelCount = Math.min(24, Math.ceil(totalHours / 2))
    const interval = totalHours / labelCount

    for (let i = 0; i <= labelCount; i++) {
      labels.push(rangeStart.clone().add(i * interval, 'hours'))
    }
    return labels
  }

  // 计算密度
  const calculateDensity = () => {
    const ganttData = generateGanttData()
    const { start: rangeStart, end: rangeEnd, totalHours } = getDateRange()
    const densityArray = Array(Math.ceil(totalHours)).fill(0)

    ganttData.forEach((item) => {
      const currentHour = item.fullStartTime.clone().startOf('hour')
      while (currentHour.isBefore(item.fullEndTime)) {
        const hourIndex = Math.floor(currentHour.diff(rangeStart, 'hours', true))
        if (hourIndex >= 0 && hourIndex < densityArray.length) {
          densityArray[hourIndex]++
        }
        currentHour.add(1, 'hour')
      }
    })

    return { densityArray, maxDensity: Math.max(...densityArray, 1) }
  }

  const getDensityColor = (count: number, max: number) => {
    if (count === 0) return 'transparent'
    const intensity = count / max
    if (intensity > 0.7) return 'rgba(239, 68, 68, 0.3)'
    if (intensity > 0.4) return 'rgba(251, 146, 60, 0.3)'
    return 'rgba(59, 130, 246, 0.2)'
  }

  const ganttData = generateGanttData()
  const timeLabels = generateTimeLabels()
  const { start: rangeStart, end: rangeEnd, center, totalHours } = getDateRange()
  const { densityArray, maxDensity } = calculateDensity()

  const stats = {
    total: ganttData.length,
    conflicts: ganttData.filter((g) => g.isConflict).length,
    running: ganttData.filter((g) => g.runningStatus === 'runing').length,
    active: ganttData.filter((g) => g.windowStatus === 'active').length,
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <h1 className="text-2xl font-bold mb-6">
        <BarChart3 className="inline-block mr-2 h-6 w-6" />
        任务窗口时间轴
      </h1>

      {/* 控制栏 */}
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* 导航控制 */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">时间轴导航:</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moveTimeWindow(-1)}
                  title="快退"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setTimeWindowCenter(timeWindowCenter.clone().subtract(1, 'hour'))
                    setAutoFollowNow(false)
                  }}
                  title="后退1小时"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant={autoFollowNow ? 'default' : 'outline'}
                  onClick={jumpToNow}
                >
                  <Clock className="h-4 w-4 mr-1" />
                  现在
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setTimeWindowCenter(timeWindowCenter.clone().add(1, 'hour'))
                    setAutoFollowNow(false)
                  }}
                  title="前进1小时"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moveTimeWindow(1)}
                  title="快进"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm">自动跟随:</span>
                <Switch checked={autoFollowNow} onCheckedChange={setAutoFollowNow} />
              </div>
            </div>

            {/* 缩放控制 */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">窗口大小:</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => zoomTimeWindow(1.5)}
                  title="放大"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => zoomTimeWindow(0.67)}
                  title="缩小"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>

              <span className="text-sm">预设:</span>
              <Select
                value={timeWindowSize.toString()}
                onValueChange={(value) => setTimeWindowSize(parseInt(value))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6小时</SelectItem>
                  <SelectItem value="12">12小时</SelectItem>
                  <SelectItem value="24">24小时</SelectItem>
                  <SelectItem value="48">48小时</SelectItem>
                  <SelectItem value="72">3天</SelectItem>
                  <SelectItem value="168">7天</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm">密度热力图:</span>
                <Switch checked={showDensityHeatmap} onCheckedChange={setShowDensityHeatmap} />
                <Button size="sm" variant="outline" onClick={fetchTasks}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* 信息栏 */}
            <Alert>
              <AlertDescription className="flex gap-4 text-sm">
                <span>📍 中心: {center.format('YYYY-MM-DD HH:mm')}</span>
                <span>⏰ 范围: {totalHours}小时 ({(totalHours / 24).toFixed(1)}天)</span>
                <span>📊 任务数: {ganttData.length}</span>
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>

      {/* 图例 */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <Badge variant="outline" className="bg-green-100 text-green-800">可用窗口</Badge>
        <Badge variant="outline" className="bg-blue-100 text-blue-800">活动窗口</Badge>
        <Badge variant="outline" className="bg-gray-100 text-gray-800">过期窗口</Badge>
        <Badge variant="outline" className="bg-orange-100 text-orange-800">预定启动</Badge>
        <Badge variant="outline" className="bg-gray-50 text-gray-600">冷却中</Badge>
        <Badge variant="outline" className="bg-pink-100 text-pink-800">等待启动</Badge>
        <Badge variant="outline" className="bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          状态冲突
        </Badge>
      </div>

      {/* Gantt 图表 */}
      <div className="border rounded-lg overflow-hidden">
        {/* 时间轴表头 */}
        <div className="flex bg-muted border-b">
          <div className="w-48 p-3 font-semibold border-r">任务名称</div>
          <div className="flex-1 flex">
            {timeLabels.map((time, idx) => {
              const isNow = time.isSame(moment(), 'hour')
              return (
                <div
                  key={idx}
                  className={`flex-1 p-2 text-center text-xs border-r last:border-r-0 ${
                    isNow ? 'bg-blue-50 font-bold' : ''
                  }`}
                >
                  {totalHours <= 12
                    ? time.format('HH:mm')
                    : totalHours <= 48
                    ? time.format('DD日HH:mm')
                    : time.format('MM-DD')}
                  {isNow && <div className="text-[10px] text-blue-600">现在</div>}
                </div>
              )
            })}
          </div>
        </div>

        {/* 任务列表 */}
        <div className="max-h-[600px] overflow-y-auto">
          {ganttData.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              当前时间范围内暂无任务窗口数据
            </div>
          ) : (
            ganttData.map((item, index) => (
              <div
                key={`${item.taskName}-${index}`}
                className="flex items-center min-h-[48px] border-b last:border-b-0"
              >
                {/* 任务名称 */}
                <div className="w-48 p-3 border-r">
                  <div className="font-medium text-sm">{item.taskName}</div>
                  <div className="flex gap-1 mt-1">
                    <Badge variant="secondary" className="text-[10px] h-4">
                      {item.platform.toUpperCase()}
                    </Badge>
                    {item.isConflict && (
                      <Badge variant="destructive" className="text-[10px] h-4">
                        <AlertTriangle className="h-3 w-3" />
                      </Badge>
                    )}
                  </div>
                </div>

                {/* 时间轴区域 */}
                <div className="flex-1 relative h-12">
                  {/* 密度热力图背景 */}
                  {showDensityHeatmap && (
                    <div className="absolute inset-0 flex">
                      {densityArray.map((count, idx) => (
                        <div
                          key={idx}
                          className="flex-1 border-r last:border-r-0"
                          style={{
                            backgroundColor: getDensityColor(count, maxDensity),
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* 当前时间指示线 */}
                  {(() => {
                    const now = moment()
                    if (now.isBetween(rangeStart, rangeEnd)) {
                      const nowPercent = (now.diff(rangeStart, 'minutes') / (totalHours * 60)) * 100
                      return (
                        <div
                          className="absolute top-0 bottom-0 w-0.5 bg-blue-500 z-20"
                          style={{ left: `${nowPercent}%` }}
                          title={`当前时间: ${now.format('HH:mm')}`}
                        />
                      )
                    }
                    return null
                  })()}

                  {/* 任务时间条 */}
                  <div
                    className="absolute top-2 h-8 rounded cursor-pointer z-10 flex items-center justify-center overflow-hidden"
                    style={{
                      left: `${item.startOffsetPercent}%`,
                      width: `${item.durationPercent}%`,
                      backgroundColor: item.isConflict ? '#ef4444' : item.windowColor,
                      border:
                        item.runningStatus === 'runing'
                          ? '2px solid #22c55e'
                          : '1px solid #d4d4d8',
                      boxShadow:
                        item.runningStatus === 'runing'
                          ? '0 0 4px rgba(34, 197, 94, 0.5)'
                          : 'none',
                    }}
                    title={`${item.taskName} | ${item.startTime} - ${item.endTime} | ${item.windowStatus} / ${item.runningStatus}`}
                  >
                    <span className="text-[10px] font-semibold text-white px-2 truncate">
                      {totalHours <= 24
                        ? `${item.startTime.slice(-5)}-${item.endTime}`
                        : item.taskName}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 统计信息 */}
      <div className="grid grid-cols-4 gap-4 mt-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">显示任务</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">窗口冲突</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.conflicts}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">正在运行</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.running}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-600">活动窗口</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.active}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
